import { tenantHeaders } from './apiClient';
import { apiRequest } from './httpRequest';
import type { ChemMoleculeV1Document } from '@peruselab/structure-editor/domain/moleculeProjection';
import type { StructureCorrespondence } from '@peruselab/structure-editor/domain/structureIdRemap';

export interface MoleculeResponse {
  readonly id: string;
  readonly documentRevision: number;
  readonly schemaVersion: string;
  readonly document: ChemMoleculeV1Document;
  readonly ownerType: string;
  readonly ownerId: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** #615 -- on create, when an identical Molecule was reused and its atoms are numbered
   *  differently: submitted ID -> stored ID. Absent/null otherwise. */
  readonly correspondence?: StructureCorrespondence | null;
}

// #147: creating a Molecule directly, needed by the client save/load rewrite. Corpus-wide dedup
// (#141/#143) runs server-side on every call, so this is safe to POST even when the constitution
// is unchanged from a prior save -- the server reuses the existing row rather than duplicating it.
export async function createMolecule(document: ChemMoleculeV1Document): Promise<MoleculeResponse> {
  return apiRequest<MoleculeResponse>('/molecules', 'molecule', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(document)
  });
}

export async function getMolecule(id: string): Promise<MoleculeResponse> {
  return apiRequest<MoleculeResponse>(`/molecules/${encodeURIComponent(id)}`, 'molecule');
}

/**
 * #1064 (ADR 0019 D5-D9): what a molecule just drawn turned out to be -- already `yours`, `public`
 * (perhaps just imported from PubChem) or `new` to you, each with its id; or `pending` (PubChem
 * couldn't be asked; it is queued, no id yet). Registered for your space, private and provisional
 * until something you save uses it.
 */
export interface DrawnMolecule {
  readonly id: string | null;
  /** #1066 step 3: the Molecule's revision, which the Isomer drawn with it is registered against. */
  readonly documentRevision?: number | null;
  readonly found: 'yours' | 'public' | 'new' | 'pending';
  readonly correspondence?: StructureCorrespondence | null;
}

export async function registerDrawnMolecule(document: ChemMoleculeV1Document): Promise<DrawnMolecule> {
  return apiRequest<DrawnMolecule>('/molecules/drawn', 'molecule', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(document)
  });
}

/** ADR 0017 (#904): a Molecule's name and identifiers for its page. */
export interface MoleculeSummary {
  readonly id: string;
  /** PubChem's name; null when PubChem has no record. */
  readonly name: string | null;
  /** Computed from the structure (D5), so a new structure has these too; null if it couldn't be. */
  readonly smiles: string | null;
  /** ADR 0023 D2 (#1113): the standard InChI and InChIKey, computed; null if they couldn't be. */
  readonly inchi: string | null;
  readonly inchiKey: string | null;
  readonly molecularFormula: string | null;
  /** #1115: the net formal charge; null if it couldn't be computed. */
  readonly formalCharge: number | null;
  readonly molecularWeight: number | null;
  /** From PubChem; null when it has no record. */
  readonly cas: string | null;
  readonly pubchemCid: string | null;
  /** ADR 0026 Q1 (#1162): it has a hapto bond, which SMILES can't express -- `smiles` are its pieces. */
  readonly haptoBonds?: boolean;
}

export async function getMoleculeSummary(id: string): Promise<MoleculeSummary> {
  return apiRequest<MoleculeSummary>(`/molecules/${encodeURIComponent(id)}/summary`, 'molecule');
}

/**
 * #1060 (ADR 0019): how a Molecule stands for the caller. `visibility` is public when anyone's
 * registration of it is; `yours` is the active tenant's own registration (null: none); `canPublish`
 * when that one is private and the caller may publish it (an administrator of the tenant).
 */
export interface MoleculeRegistration {
  readonly visibility: 'public' | 'private';
  readonly yours: 'public' | 'private' | null;
  readonly canPublish: boolean;
  /** Public and the caller may make it private again (an accidental publish is undoable). */
  readonly canUnpublish: boolean;
  /** #1064: your space drew it, but nothing you saved uses it yet. */
  readonly provisional: boolean;
  /** #1064 4c: your space's registration is the one that brought it into PeruseLab. */
  readonly firstInPeruseLab?: boolean;
  /** #1064 4c: your space's members' own contributions (under its "who sees" setting); nobody else's. */
  readonly contributions?: readonly MoleculeContribution[];
  /** #1064 4c (A1): an outside record found after your space created it -- evidence, nothing taken away. */
  readonly externalRecord?: {
    readonly source: string;
    readonly reference: string | null;
    readonly foundAt: string;
  } | null;
}

export interface MoleculeContribution {
  /** The member's display name; null when they haven't one. */
  readonly name: string | null;
  readonly you: boolean;
  readonly kind: 'created' | 'reused';
  readonly at: string;
}

export async function getMoleculeRegistration(id: string): Promise<MoleculeRegistration> {
  return apiRequest<MoleculeRegistration>(`/molecules/${encodeURIComponent(id)}/registration`, 'molecule');
}

/** #1060 (D13): make the tenant's own registration public; `tenant` pins it (see tenantHeaders). */
export async function publishMolecule(id: string, tenant?: string | null): Promise<void> {
  return apiRequest<void>(`/molecules/${encodeURIComponent(id)}/publish`, 'molecule', {
    method: 'POST',
    headers: tenantHeaders(tenant)
  });
}

/** #1060: the undo of publishMolecule -- the tenant's own registration goes back to private. */
export async function unpublishMolecule(id: string, tenant?: string | null): Promise<void> {
  return apiRequest<void>(`/molecules/${encodeURIComponent(id)}/unpublish`, 'molecule', {
    method: 'POST',
    headers: tenantHeaders(tenant)
  });
}

/** ADR 0017 (#906): one statement PubChem holds about a compound, with its source. */
export interface PublishedStatement {
  /** PubChem's heading ("Record Description", "Use Classification"). */
  readonly section: string;
  readonly text: string;
  readonly source: string;
  readonly sourceUrl: string | null;
  /** #947: set when the statement is an assertion -- Discuss anchors to it, history and corrections apply. */
  readonly assertionId?: string;
  /** #947 phase 2: a person's statement -- theirs to correct or withdraw, and how it stands. */
  readonly mine?: boolean;
  readonly standing?: string;
  readonly audience?: 'PUBLIC' | 'PRIVATE';
  readonly language?: string;
}

/** A source whose words aren't shown (its licence doesn't allow it), as a name and a link (D3). */
export interface PublishedSource {
  readonly name: string;
  readonly url: string | null;
}

/** #908: one GHS hazard as a fact -- its code ("H302") and, for an aggregate, the share of reports. */
export interface GhsHazard {
  readonly code: string;
  readonly percent: number | null;
}

/** #908: one classifier's GHS classification, as codes; the page supplies the standard wording. */
export interface GhsClassification {
  readonly source: string;
  readonly sourceUrl: string | null;
  /** "GHS01" … "GHS09". */
  readonly pictograms: readonly string[];
  /** "Warning" or "Danger"; null if none. */
  readonly signal: string | null;
  readonly hazards: readonly GhsHazard[];
  /** How many notifications an aggregate covers (ECHA's C&L Inventory); null otherwise. */
  readonly reports: number | null;
}

/**
 * ADR 0017 phase 2 (#906): a Molecule's published facts. `ready`: below; `pending`: a first fetch is
 * running, ask again shortly; `none`: PubChem has no record; `unavailable`: couldn't be reached;
 * `implausible` (#1079, ADR 0020 D9): not looked up -- the structure check holds it back for `heldBack`.
 */
export interface MoleculeFacts {
  readonly status: 'ready' | 'pending' | 'none' | 'unavailable' | 'implausible';
  readonly pubchemCid: number | null;
  readonly title: string | null;
  readonly inchiKey: string | null;
  readonly synonyms: readonly string[];
  readonly about: readonly PublishedStatement[];
  readonly uses: readonly PublishedStatement[];
  readonly otherSources: readonly PublishedSource[];
  /** #908: Safety -- GHS classifications (codes only) and the toxicity summary. */
  readonly ghs: readonly GhsClassification[];
  readonly toxicity: readonly PublishedStatement[];
  readonly fetchedAt: string | null;
  /** #1101: with `implausible`, the check's findings that hold it back, as its own messages. */
  readonly heldBack: readonly {
    readonly rule: string;
    readonly messageKey: string;
    readonly args: Readonly<Record<string, string>>;
  }[];
}

/** #1086: one linked molecule -- a salt's component (with its count), or a salt an ion is part of. */
export interface MoleculeComponentEntry {
  /** #1115: null for a salt's ion the reader may not open -- listed (it's in the salt), not linked. */
  readonly id: string | null;
  readonly count: number;
  readonly name: string | null;
  readonly molecularFormula: string | null;
  readonly formalCharge: number | null;
}

/** #1086: what a salt is made of, and the salts a molecule is part of (only ones you may open). */
export interface MoleculeComponents {
  readonly components: readonly MoleculeComponentEntry[];
  readonly salts: readonly MoleculeComponentEntry[];
}

export async function getMoleculeComponents(id: string): Promise<MoleculeComponents> {
  return apiRequest<MoleculeComponents>(`/molecules/${encodeURIComponent(id)}/components`, 'molecule');
}

/** ADR 0020 D7 (#1106): a molecule's recorded structure-check result. */
export interface MoleculeStructureCheck {
  readonly checkedAt: string;
  readonly engine: string;
  readonly engineVersion: string;
  readonly rulesVersion: string;
  readonly findings: readonly {
    readonly rule: string;
    readonly severity: 'ERROR' | 'WARNING' | 'INFO';
    readonly messageKey: string;
    readonly args: Readonly<Record<string, string>>;
    /** A warning its author confirmed intended: by the reader (`byYou`) or not ("its author"). */
    readonly confirmation: { readonly byYou: boolean; readonly at: string } | null;
  }[];
}

/** Null when the structure couldn't be checked (204). */
export async function getMoleculeStructureCheck(id: string): Promise<MoleculeStructureCheck | null> {
  return (
    (await apiRequest<MoleculeStructureCheck | undefined>(
      `/molecules/${encodeURIComponent(id)}/structure-check`,
      'molecule'
    )) ?? null
  );
}

export async function getMoleculeFacts(id: string): Promise<MoleculeFacts> {
  return apiRequest<MoleculeFacts>(`/molecules/${encodeURIComponent(id)}/facts`, 'molecule');
}
